import { useState } from "react";
import { Box, Button, HStack, Input, Stack, Text } from "@chakra-ui/react";
import type { EffectiveReferralTerms, ReferralTerms } from "../api/events";

const EMPTY: ReferralTerms = { referrer_credits: 0, referee_bonus_works: 0 };

function toNonNegativeInt(raw: string): number {
  const n = raw.trim() === "" ? 0 : Math.trunc(Number(raw));
  return Number.isNaN(n) || n < 0 ? 0 : n;
}

/** Referrer credits + referee bonus works, at whichever scope the caller owns
 * (creator or origin_event). `value === null` (or `undefined`) means "no
 * override at this scope" — resolve_referral_terms falls through to the next
 * cascade tier. Editing either field for the first time promotes null -> an
 * explicit {referrer_credits: 0, referee_bonus_works: 0} object (0 is a
 * valid, deliberate "grants nothing" policy — distinct from unset).
 *
 * A null field renders EMPTY with placeholder="inherits", rather than
 * displaying "0" — that display was the root cause of two prior bugs here:
 * "operator typed 0" and "operator tabbed through untouched" were previously
 * indistinguishable (both showed "0"), which is why a dirty-flag mechanism
 * used to exist. With unset rendered as empty text, the commit logic is
 * fully stateless — no dirty flags needed:
 *
 *   1. value is null/undefined AND the raw input is empty (trimmed) ->
 *      no commit. Bare focus/blur or tab-through on an untouched field.
 *   2. value is null/undefined AND the raw input has a number -> commit.
 *      The operator deliberately set something, including an explicit 0.
 *   3. value is an explicit pair -> build the candidate and commit only if
 *      it differs from `value`, field by field. Editing a field and
 *      reverting it to its original number (e.g. 3 -> 5 -> 3) naturally
 *      produces a candidate that deep-equals `value`, so it no-ops "for
 *      free" — no history tracking needed. If the candidate matches
 *      `value`, the local input text is resynced to what's actually stored
 *      (e.g. after clearing a field on an existing override, or typing a
 *      leading zero over an unchanged digit) so the box never visibly
 *      disagrees with the value — which would recreate the exact
 *      unset-vs-explicit-zero ambiguity this component exists to avoid.
 */
export function ReferralTermsFields({
  value,
  onChange,
  editing,
  clearDisabledReason,
  effective,
}: {
  value: ReferralTerms | null;
  onChange: (v: ReferralTerms | null) => void;
  editing: boolean;
  /** What the cascade actually resolves to at this scope, if the caller can
   * work it out. When supplied, the read-only view shows those numbers and
   * their source instead of "— (inherits from the cascade)", which is true but
   * unactionable. Optional: callers that can't resolve it (the event editor)
   * keep the old text. Only affects display — never what gets committed. */
  effective?: EffectiveReferralTerms | null;
  /** When set, the "Clear override" affordance renders disabled with this
   * reason instead of being actionable — used for General/Default, the
   * referral-terms cascade floor, which must never be left unconfigured. */
  clearDisabledReason?: string | null;
}) {
  const v = value ?? EMPTY;
  // `!= null`, matching `commit` and the two placeholder checks below: with
  // `!== null` an `undefined` value renders empty inputs with the "inherits"
  // placeholder while still claiming to be set, so the Clear-override button
  // appears and the read-only branch prints "0 credits ... +0 bonus works"
  // instead of "— (inherits from the cascade)". TS says undefined can't get
  // here; `commit` tolerates it anyway, so both guards agree on it.
  const isSet = value != null;

  // Displayed text for each field: empty (placeholder "inherits") when
  // unset, the real number otherwise — including a genuine 0. Tracked via
  // the same render-phase "did the prop change under us" comparison as
  // before, so switching scopes (a different origin_event / creator, or a
  // real commit round-tripping through the parent's state) resets the local
  // input text without needing an effect.
  const rcDisplay = value == null ? "" : String(value.referrer_credits);
  const [rc, setRc] = useState(rcDisplay);
  const [prevRcDisplay, setPrevRcDisplay] = useState(rcDisplay);
  if (rcDisplay !== prevRcDisplay) {
    setPrevRcDisplay(rcDisplay);
    setRc(rcDisplay);
  }
  const bwDisplay = value == null ? "" : String(value.referee_bonus_works);
  const [bw, setBw] = useState(bwDisplay);
  const [prevBwDisplay, setPrevBwDisplay] = useState(bwDisplay);
  if (bwDisplay !== prevBwDisplay) {
    setPrevBwDisplay(bwDisplay);
    setBw(bwDisplay);
  }

  const commit = (k: "referrer_credits" | "referee_bonus_works", raw: string) => {
    // `== null` (not `!== null`): a caller passing `undefined` (e.g. an API
    // payload that omits referral_terms) must be tolerated the same way as
    // an explicit null, not throw on `value.referrer_credits`.
    if (value == null) {
      if (raw.trim() === "") {
        // rule 1: untouched null field, no commit. Resync the local text to
        // "" (not whatever whitespace the operator left behind) so the box
        // still shows the "inherits" placeholder — a lingering " " renders
        // visually blank but is not the empty string, which silently
        // suppresses the placeholder.
        if (k === "referrer_credits") {
          setRc("");
        } else {
          setBw("");
        }
        return;
      }
      // rule 2: a genuine edit starting from null always commits, even if
      // the operator's final digits happen to be 0 — see the component
      // docstring for why null and {0,0} must never be conflated.
      onChange({ ...EMPTY, [k]: toNonNegativeInt(raw) });
      return;
    }
    // rule 3: value is an explicit pair — commit only if the candidate
    // actually differs from it.
    const candidate: ReferralTerms = { ...value, [k]: toNonNegativeInt(raw) };
    if (
      candidate.referrer_credits === value.referrer_credits &&
      candidate.referee_bonus_works === value.referee_bonus_works
    ) {
      if (k === "referrer_credits") {
        setRc(String(value.referrer_credits));
      } else {
        setBw(String(value.referee_bonus_works));
      }
      return;
    }
    onChange(candidate);
  };

  if (!editing) {
    // Prefer the resolved cascade whenever the caller could work it out — it
    // covers the override case too (resolve returns the creator's own row, and
    // labels it "individual"), so there is no need to branch on isSet here.
    // Both numbers are credits despite the schema calling one *_works.
    if (effective) {
      return (
        <Text fontSize="sm">
          referrer {effective.referrer_credits} / referee{" "}
          {effective.referee_bonus_works}{" "}
          <Text as="span" color="gray.500">
            ({effective.source})
          </Text>
        </Text>
      );
    }
    return (
      <Text fontSize="sm">
        {isSet ? (
          <>
            {v.referrer_credits} credit{v.referrer_credits === 1 ? "" : "s"} to referrer,{" "}
            +{v.referee_bonus_works} bonus work{v.referee_bonus_works === 1 ? "" : "s"} for
            referee
          </>
        ) : (
          <Text as="span" color="gray.500">
            — (inherits from the cascade)
          </Text>
        )}
      </Text>
    );
  }

  return (
    <Stack spacing={3}>
      <HStack align="flex-start" spacing={4}>
        <Box flex={1}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Referrer credits per conversion
          </Text>
          <Input
            size="sm"
            type="number"
            min={0}
            value={rc}
            placeholder={value == null ? "inherits" : undefined}
            onChange={(e) => setRc(e.target.value)}
            onBlur={() => commit("referrer_credits", rc)}
          />
        </Box>
        <Box flex={1}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Referred creator bonus works
          </Text>
          <Input
            size="sm"
            type="number"
            min={0}
            value={bw}
            placeholder={value == null ? "inherits" : undefined}
            onChange={(e) => setBw(e.target.value)}
            onBlur={() => commit("referee_bonus_works", bw)}
          />
        </Box>
      </HStack>
      <Text fontSize="xs" color="gray.400">
        0 is a valid policy — "grants nothing at this scope" — distinct from no override at
        all.
      </Text>
      {isSet && (
        <HStack>
          <Button
            size="xs"
            variant="ghost"
            isDisabled={!!clearDisabledReason}
            onClick={() => onChange(null)}
          >
            Clear override
          </Button>
          {clearDisabledReason && (
            <Text fontSize="xs" color="gray.400">
              {clearDisabledReason}
            </Text>
          )}
        </HStack>
      )}
    </Stack>
  );
}
